import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Library, Wand2, PlusCircle, PersonStanding, UserRound } from 'lucide-react';
import type { Client } from '@/lib/types';
import type { ClientIntelligence } from '@/lib/clients/roster-intelligence';
import { NeedsTagBadges } from './NeedsTagBadges';
import { RosterFlagBadges } from './RosterFlagBadges';
import { cn } from '@/lib/utils';

interface ClientRosterExpandedPanelProps {
  client: Client;
  intelligence?: ClientIntelligence;
  onOpenLibrary: () => void;
  onOpenPlanner: () => void;
  onStartPosture: () => void;
}

/**
 * Expanded summary strip for the selected client on the roster (Option A).
 * Surfaces needs, status flags, and quick actions — hidden for all other
 * compact rows.
 */
export function ClientRosterExpandedPanel({
  client,
  intelligence,
  onOpenLibrary,
  onOpenPlanner,
  onStartPosture,
}: ClientRosterExpandedPanelProps) {
  const hasNeeds = (client.needsTags?.length ?? 0) > 0;
  const action = intelligence?.nextBestAction;
  const showPrimaryAction = action && action.kind !== 'review';

  // Resolve the recommended next action to a single primary CTA (label + icon +
  // destination), so it is not buried among the secondary actions. Routing
  // mirrors the roster chip (Phase 3.6): reshoot → posture flow,
  // start-assessment/reassess → SOP intake for this client.
  const primaryAction =
    showPrimaryAction && action
      ? action.kind === 'reshoot'
        ? { label: action.label, Icon: PersonStanding, onClick: onStartPosture, href: undefined as string | undefined }
        : { label: action.label, Icon: PlusCircle, onClick: undefined, href: `/clients/${client.id}/assessment/new` as string | undefined }
      : null;

  // Shared button chrome: 44px target, 8px radius, 8px icon gap (design guardrails).
  const actionBtn = 'h-11 rounded-lg px-4';
  const actionIcon = 'h-4 w-4 mr-2';

  return (
    <div
      className="border-t border-primary/20 px-4 py-3 space-y-3"
      data-testid="client-roster-expanded"
    >
      <div className="flex flex-wrap items-start gap-x-6 gap-y-2 text-sm">
        {hasNeeds && (
          <div className="space-y-1 min-w-0">
            <p className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
              Needs
            </p>
            <NeedsTagBadges needsTags={client.needsTags} max={12} />
          </div>
        )}
        {intelligence && (intelligence.flags.length > 0 || showPrimaryAction) && (
          <div className="space-y-1 min-w-0">
            <p className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
              Status
            </p>
            <RosterFlagBadges intelligence={intelligence} hideAction />
          </div>
        )}
        {!hasNeeds && !intelligence?.flags.length && (
          <p className="text-sm text-muted-foreground italic">
            No needs or flags — open Client 360 to curate prescription tags.
          </p>
        )}
      </div>

      {/* Quick actions only. The full client folder (Client 360) is reached by clicking
          the client name or the ⋯ menu, so it is intentionally not repeated here. */}
      <div className="flex flex-wrap items-center gap-2 pt-1">
        {/* Primary CTA — recommended next action. Full-width on mobile, keeps its label
            so "what to do next" is always legible; inline from sm up. */}
        {primaryAction &&
          (primaryAction.href ? (
            <Button type="button" size="sm" className={cn(actionBtn, 'w-full sm:w-auto')} asChild>
              <Link to={primaryAction.href}>
                <primaryAction.Icon className={actionIcon} />
                {primaryAction.label}
              </Link>
            </Button>
          ) : (
            <Button
              type="button"
              size="sm"
              className={cn(actionBtn, 'w-full sm:w-auto')}
              onClick={primaryAction.onClick}
            >
              <primaryAction.Icon className={actionIcon} />
              {primaryAction.label}
            </Button>
          ))}

        {/* Secondary quick actions — icon-only on mobile (< sm), icon + label from sm up. */}
        <Button
          type="button"
          variant="outline"
          size="sm"
          className={actionBtn}
          onClick={onOpenPlanner}
          aria-label="Plan drills"
          title="Plan drills"
        >
          <Wand2 className="h-4 w-4 sm:mr-2" />
          <span className="hidden sm:inline">Plan drills</span>
        </Button>
        <Button
          type="button"
          variant="outline"
          size="sm"
          className={actionBtn}
          onClick={onOpenLibrary}
          aria-label="Open library"
          title="Open library"
        >
          <Library className="h-4 w-4 sm:mr-2" />
          <span className="hidden sm:inline">Open library</span>
        </Button>

        {/* Full folder — small, muted, tucked right; icon-only on mobile. Also reachable
            via the client name and the ⋯ menu. */}
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="ml-auto h-11 rounded-lg px-3 text-muted-foreground hover:text-foreground sm:h-9"
          asChild
          aria-label="Client 360"
          title="Client 360"
        >
          <Link to={`/clients/${client.id}`}>
            <UserRound className="h-4 w-4 sm:mr-1.5" />
            <span className="hidden sm:inline">Client 360</span>
          </Link>
        </Button>
      </div>
    </div>
  );
}
